import { useCallback, useEffect, useState } from 'react';
import {
  createFormateur,
  updateFormateur,
  deposerPieceFormateur,
  extraireFormateur,
  getPiecesFormateur,
  urlPieceFormateur,
} from '../services/formateurService';
import { etatEcheance } from './PiecesFormateur';
import SelectRecherche from '../components/SelectRecherche';
import { FORMATS_ACCEPTES } from './categoriesDocument';
import BarreProgression, { useProgression } from '../components/BarreProgression';
import RemplirParTexte from '../components/RemplirParTexte';
import '../assets/styles/Parametrage.css';

const VIDE = {
  civilite: '',
  prenom: '',
  nom: '',
  email: '',
  telephone: '',
  fonction: '',
  specialites: '',
  adresse: '',
  notes: '',
  // Rémunération par journée (€ HT), imprimée sur le contrat du formateur.
  tarifJournalierHt: '',
  actif: true,
};

function versForm(f) {
  return {
    civilite: f.civilite ?? '',
    prenom: f.prenom ?? '',
    nom: f.nom ?? '',
    email: f.email ?? '',
    telephone: f.telephone ?? '',
    fonction: f.fonction ?? '',
    specialites: f.specialites ?? '',
    adresse: f.adresse ?? '',
    notes: f.notes ?? '',
    tarifJournalierHt: f.tarifJournalierHt ?? '',
    actif: f.actif ?? true,
  };
}

/**
 * La nature d'une pièce, devinée dans son nom de fichier ou son libellé
 * (migrations 080 et 083). L'URSSAF et la RC Pro portent une date
 * d'échéance : c'est elle qui déclenche l'alerte de renouvellement, et le
 * serveur la lit dans le fichier quand elle n'est pas saisie.
 */
const NATURES = [
  { type: 'cv', libelle: 'CV', motif: /(^|[^a-z])cv([^a-z]|$)|curriculum/ },
  { type: 'urssaf', libelle: 'Attestation URSSAF', echeance: true, motif: /urssaf|vigilance/ },
  {
    type: 'rc_pro',
    libelle: 'Assurance RC Pro',
    echeance: true,
    motif: /rc[ _-]?pro|responsabilite civile|assurance/,
  },
  {
    type: 'diplome',
    libelle: 'Diplôme',
    motif: /diplome|master|licence|doctorat|certificat|certification|(^|[^a-z])(bts|dut|deust)([^a-z]|$)/,
  },
  { type: 'permis', libelle: 'Permis de conduire', motif: /permis/ },
  { type: 'carte_grise', libelle: 'Carte grise', motif: /carte[ _-]?grise|certificat d.immatriculation/ },
  { type: 'notation_of', libelle: 'Notation stagiaires', motif: /notation|evaluation|avis stagiaires/ },
];
const NATURE_AUTRE = { type: 'autre', libelle: 'Autre pièce' };

/** Minuscules sans accents : la devinette pardonne l'orthographe. */
const simplifier = (texte) =>
  String(texte || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

const natureDe = (texte) => NATURES.find((n) => n.motif.test(simplifier(texte))) || NATURE_AUTRE;

/** « attestation-urssaf_recto.pdf » → « Attestation urssaf » : le libellé proposé. */
function libelleDepuisNom(nom) {
  const base = String(nom || '')
    .replace(/\.[^.]+$/, '')
    .replace(/recto|verso|face|avant|arriere|arrière/gi, ' ')
    .replace(/[-_.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return base ? base.charAt(0).toUpperCase() + base.slice(1) : 'Pièce';
}

/**
 * Clé de regroupement d'un fichier : son libellé déduit, sans les numéros.
 * « permis-recto.jpg » et « permis-verso.jpg » (ou « diplome 1 » /
 * « diplome 2 ») partagent la même clé : ils forment UNE pièce.
 */
const cleGroupe = (nom) =>
  simplifier(libelleDepuisNom(nom))
    .replace(/\b\d+\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// La clé d'une ligne suit la ligne tant qu'elle existe.
let compteurPieces = 0;

/**
 * Aperçu d'un fichier encore local (pas encore déposé) : ouvert dans un
 * onglet. PDF et images s'affichent ; un Word, que le navigateur ne sait
 * pas rendre, se télécharge.
 */
function apercuFichier(fichier) {
  const url = URL.createObjectURL(fichier);
  window.open(url, '_blank', 'noopener');
  // Large : le temps que l'onglet charge, puis l'URL temporaire est rendue.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/** Les champs de la fiche que l'IA peut remplir, avec leur nom en clair. */
const CHAMPS_IA = {
  civilite: 'civilité',
  prenom: 'prénom',
  nom: 'nom',
  email: 'email',
  telephone: 'téléphone',
  fonction: 'fonction',
  specialites: 'spécialités',
  adresse: 'adresse',
};

/**
 * Modale de création ou de modification d'une fiche formateur, partagée par
 * la liste et par la fiche.
 *
 * `formateur` null = création. `onEnregistre` reçoit la fiche renvoyée par
 * le serveur — c'est elle qui fait foi, pas le formulaire.
 *
 * Les pièces tiennent en deux gestes : le CV d'abord (dès qu'il est choisi,
 * l'IA le lit et remplit les champs vides ; son dépôt produit le CV
 * anonyme), puis UN champ multi-fichiers pour tout le reste. La nature et
 * le libellé de chaque fichier sont déduits de son nom — corrigeables —,
 * un recto-verso est regroupé en une seule pièce, et les attestations
 * montrent leur champ d'échéance. Tout est déposé après l'enregistrement
 * de la fiche.
 *
 * En modification, les pièces déjà au dossier s'affichent, ouvrables d'un
 * clic : un champ de fichier ne peut pas être prérempli par le navigateur.
 */
function FormateurFormulaire({ formateur = null, onClose, onEnregistre, espaceFormateur = false }) {
  const [form, setForm] = useState(formateur ? versForm(formateur) : VIDE);
  // Le CV à part : c'est lui qui déclenche la lecture par l'IA.
  const [cv, setCv] = useState(null);
  // Toutes les autres pièces : { cle, cleGroupe, libelle, type, echeance, fichiers[] }.
  const [pieces, setPieces] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lecture, setLecture] = useState(false);
  const [erreur, setErreur] = useState(null);
  // Ce que la lecture du CV a donné, affiché sous son champ.
  const [retourCv, setRetourCv] = useState(null); // { ok, texte }
  const progressionIa = useProgression(lecture, { estimeeMs: 15000 });
  // Enregistrement : la fiche d'abord (étape 0), puis une pièce à la fois.
  const [depot, setDepot] = useState(null);
  // Chaque étape avance d'elle-même pendant qu'elle tourne (le CV attend son
  // CV anonyme une dizaine de secondes) : sans cela la barre resterait à 0,
  // donc invisible, pendant toute la première.
  const avanceEtape = useProgression(depot ? depot.fait + 1 : 0, {
    estimeeMs: depot && depot.type === 'cv' ? 12000 : 3000,
  });
  // Fiche déjà créée lors d'un premier essai (pièce en échec) : on la met
  // à jour au lieu d'en créer une seconde.
  const [idEnregistre, setIdEnregistre] = useState(formateur ? formateur.id : null);
  // Les pièces déjà déposées (modification d'une fiche existante).
  const [auDossier, setAuDossier] = useState([]);

  const chargerAuDossier = useCallback(
    (id) =>
      getPiecesFormateur(id)
        .then((res) => setAuDossier(res.data || []))
        .catch(() => setAuDossier([])),
    []
  );

  useEffect(() => {
    setForm(formateur ? versForm(formateur) : VIDE);
    setIdEnregistre(formateur ? formateur.id : null);
    setErreur(null);
    if (formateur) chargerAuDossier(formateur.id);
    else setAuDossier([]);
  }, [formateur, chargerAuDossier]);

  const changer = (champ) => (e) => {
    const valeur = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [champ]: valeur }));
  };

  /** Les pièces déjà au dossier pour une nature donnée. */
  const deposees = (type) => auDossier.filter((piece) => piece.type === type);

  /**
   * Les fichiers choisis rejoignent la liste : un par pièce, sauf quand la
   * clé de regroupement dit qu'ils forment un même document (recto-verso).
   */
  function ajouterPieces(e) {
    const liste = Array.from(e.target.files || []);
    e.target.value = '';
    if (!liste.length) return;
    const suite = pieces.map((p) => ({ ...p, fichiers: [...p.fichiers] }));
    // Les attestations fraîchement ajoutées partent en lecture d'échéance.
    const aLire = [];
    for (const fichier of liste) {
      const cle = cleGroupe(fichier.name);
      const groupe = cle.length > 2 ? suite.find((p) => p.cleGroupe === cle) : null;
      if (groupe) {
        if (!groupe.fichiers.some((f) => f.name === fichier.name && f.size === fichier.size)) {
          groupe.fichiers.push(fichier);
        }
      } else {
        const nature = natureDe(fichier.name);
        const entree = {
          cle: ++compteurPieces,
          cleGroupe: cle,
          libelle: libelleDepuisNom(fichier.name),
          type: nature.type,
          echeance: '',
          echeanceDemandee: false,
          fichiers: [fichier],
        };
        if (nature.echeance) {
          entree.echeanceDemandee = true;
          aLire.push({ cle: entree.cle, type: nature.type, fichier });
        }
        suite.push(entree);
      }
    }
    setPieces(suite);
    for (const l of aLire) lireEcheancePiece(l.cle, l.type, l.fichier);
  }

  /**
   * L'échéance d'une attestation se préremplit dès l'ajout : l'IA lit le
   * fichier. Introuvable (scan, image…) : le champ reste vide, à saisir.
   */
  async function lireEcheancePiece(cle, type, fichier) {
    setPieces((prev) =>
      prev.map((p) => (p.cle === cle ? { ...p, lectureEcheance: true, echeanceDemandee: true } : p))
    );
    try {
      const res = await extraireFormateur({ pieces: [{ type, fichier }] });
      const lue =
        (type === 'urssaf' ? res.data?.urssaf_echeance : res.data?.rc_pro_echeance) || '';
      setPieces((prev) =>
        prev.map((p) =>
          p.cle === cle
            ? {
                ...p,
                lectureEcheance: false,
                echeanceIntrouvable: !lue,
                // Jamais par-dessus une saisie faite pendant la lecture.
                echeance: p.echeance || lue,
              }
            : p
        )
      );
    } catch {
      setPieces((prev) =>
        prev.map((p) =>
          p.cle === cle ? { ...p, lectureEcheance: false, echeanceIntrouvable: true } : p
        )
      );
    }
  }

  /**
   * Renommer une pièce : la nature se redevine sur le nouveau libellé — et
   * une pièce requalifiée en attestation part en lecture d'échéance.
   */
  function renommerPiece(cle, libelle) {
    const piece = pieces.find((p) => p.cle === cle);
    if (!piece) return;
    const nature = natureDe(`${libelle} ${piece.fichiers[0]?.name || ''}`);
    setPieces((prev) =>
      prev.map((p) => (p.cle === cle ? { ...p, libelle, type: nature.type } : p))
    );
    if (nature.echeance && !piece.echeanceDemandee && !piece.echeance) {
      lireEcheancePiece(cle, nature.type, piece.fichiers[0]);
    }
  }

  function changerEcheancePiece(cle, echeance) {
    setPieces((prev) => prev.map((p) => (p.cle === cle ? { ...p, echeance } : p)));
  }

  /** Retire un fichier ; une pièce vidée disparaît de la liste. */
  function retirerFichierPiece(cle, fichier) {
    setPieces((prev) =>
      prev
        .map((p) => (p.cle === cle ? { ...p, fichiers: p.fichiers.filter((f) => f !== fichier) } : p))
        .filter((p) => p.fichiers.length)
    );
  }

  // Tout ce qui partira au dépôt, à plat : le CV d'abord, puis chaque
  // fichier avec le libellé de sa pièce (numéroté pour un recto-verso).
  const choisis = [
    ...(cv ? [{ type: 'cv', fichier: cv, libelle: 'CV' }] : []),
    ...pieces.flatMap((p) =>
      p.fichiers.map((fichier, i) => ({
        type: p.type,
        fichier,
        libelle:
          p.fichiers.length > 1 ? `${p.libelle} (${i + 1}/${p.fichiers.length})` : p.libelle,
        dateEcheance: p.echeance || undefined,
        cle: p.cle,
      }))
    ),
  ];

  /** L'IA lit le CV ; seuls les champs vides se remplissent. */
  async function lireCv(fichier) {
    setLecture(true);
    setErreur(null);
    try {
      const res = await extraireFormateur({ pieces: [{ type: 'cv', fichier }] });
      const lu = res.data || {};
      // Ce qui sera rempli, lu sur l'état au moment du choix ; la mise à
      // jour ne touche ensuite que les champs restés vides.
      const remplis = Object.keys(CHAMPS_IA)
        .filter((cle) => lu[cle] && !String(form[cle] || '').trim())
        .map((cle) => CHAMPS_IA[cle]);

      setForm((f) => {
        const suite = { ...f };
        for (const cle of Object.keys(CHAMPS_IA)) {
          if (lu[cle] && !String(f[cle] || '').trim()) suite[cle] = lu[cle];
        }
        return suite;
      });
      setRetourCv({
        ok: true,
        texte: remplis.length
          ? `Rempli par l’IA : ${remplis.join(', ')}. Relisez avant d’enregistrer.`
          : 'L’IA n’a rien trouvé de plus que ce qui est déjà saisi.',
      });
    } catch (err) {
      // Le CV reste choisi : il sera déposé à l'enregistrement, lu ou non.
      setRetourCv({
        ok: false,
        texte: `${err.message || 'Lecture par l’IA impossible.'} Remplissez la fiche à la main.`,
      });
    } finally {
      setLecture(false);
    }
  }

  async function soumettre(e) {
    e.preventDefault();
    setErreur(null);
    if (!form.prenom.trim() || !form.nom.trim()) {
      setErreur('Le prénom et le nom sont obligatoires.');
      return;
    }
    if (!form.email.trim()) {
      setErreur(
        "L'adresse email est obligatoire (convocation, rappels, émargement)."
      );
      return;
    }
    setSaving(true);
    const etapes = choisis.length + 1;
    setDepot({ fait: 0, total: etapes, nom: null, type: null });
    try {
      const res = idEnregistre
        ? await updateFormateur(idEnregistre, form)
        : await createFormateur(form);
      const fiche = res.data;
      setIdEnregistre(fiche.id);

      // Les pièces suivent, une à une : l'échec de l'une n'annule pas la fiche.
      // Une échéance laissée vide est lue dans l'attestation par le serveur.
      const echecs = [];
      const sansEcheance = [];
      let cvAnonymeManque = null;
      for (const [i, item] of choisis.entries()) {
        setDepot({ fait: i + 1, total: etapes, nom: item.fichier.name, type: item.type, rang: i + 1 });
        try {
          const res = await deposerPieceFormateur(fiche.id, {
            type: item.type,
            dateEcheance: item.dateEcheance,
            libelle: item.libelle || undefined,
            fichier: item.fichier,
          });
          const piece = res.data || {};
          const nature = NATURES.find((n) => n.type === item.type);
          if (nature?.echeance && !piece.dateEcheance) {
            sansEcheance.push(item.libelle || nature.libelle);
          }
          if (piece.cvAnonyme && !piece.cvAnonyme.genere) cvAnonymeManque = piece.cvAnonyme.raison;
          if (item.type === 'cv' && !item.cle) setCv(null);
          else retirerFichierPiece(item.cle, item.fichier);
        } catch (err) {
          echecs.push(`${item.libelle || item.fichier.name} : ${err.message}`);
        }
      }
      setDepot(null);
      const avis = [];
      if (sansEcheance.length) {
        avis.push(
          `${sansEcheance.join(' et ')} : échéance introuvable dans le fichier. À compléter depuis la fiche du formateur.`
        );
      }
      if (cvAnonymeManque) {
        avis.push(
          `CV déposé, mais son CV anonyme n’a pas pu être généré (${cvAnonymeManque}) À relancer depuis la fiche, onglet Pièces administratives.`
        );
      }
      if (avis.length && !echecs.length) window.alert(avis.join('\n\n'));
      if (echecs.length) {
        // Ce qui est passé figure désormais au dossier : on le montre.
        chargerAuDossier(fiche.id);
        setErreur(
          `Fiche enregistrée, mais ${echecs.length > 1 ? 'des pièces n’ont' : 'une pièce n’a'} pas pu être déposée${echecs.length > 1 ? 's' : ''} — ${echecs.join(' ; ')}. Corrigez puis enregistrez à nouveau.`
        );
        return;
      }
      if (onEnregistre) onEnregistre(fiche);
    } catch (err) {
      setErreur(err.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
      setDepot(null);
    }
  }

  const occupe = saving || lecture;

  // Les sections se numérotent dans l'ordre où elles sont rendues.
  let numero = 0;
  const section = (titre, aide, contenu) => {
    numero += 1;
    return (
      <section className="formateur-section" key={titre}>
        <div className="formateur-section-tete">
          <span className="formateur-section-numero">{numero}</span>
          <div>
            <div className="formateur-section-titre">{titre}</div>
            {aide && <div className="formateur-section-aide">{aide}</div>}
          </div>
        </div>
        {contenu}
      </section>
    );
  };

  /** Une pièce déposée : un lien qui l'ouvre, avec l'état de son échéance. */
  const lienDepose = (piece) => (
    <a
      className="formateur-fichier formateur-fichier-depose"
      key={piece.id}
      href={urlPieceFormateur(piece.id, { apercu: true })}
      target="_blank"
      rel="noopener noreferrer"
      title={`${piece.typeLibelle} — déjà au dossier, ouvrir`}
    >
      {piece.libelle && piece.libelle !== piece.typeLibelle ? piece.libelle : piece.nomFichier}
      {etatEcheance(piece.dateEcheance) ? ` · ${etatEcheance(piece.dateEcheance).libelle}` : ''}
    </a>
  );

  return (
    <div className="param-modal-overlay" onClick={occupe ? undefined : onClose}>
      <div className="param-modal" onClick={(e) => e.stopPropagation()}>
        <div className="param-modal-header">
          <h2 className="param-modal-title">
            {formateur ? 'Modifier la fiche formateur' : 'Nouveau formateur'}
          </h2>
          <button type="button" className="param-modal-close" onClick={onClose} disabled={occupe}>
            ×
          </button>
        </div>

        <form className="param-form param-modal-body" onSubmit={soumettre}>
          {/* En tête de fiche, et collée là pendant le défilement : sous le dernier champ, on ne la voyait pas. */}
          {erreur && <div className="param-form-message param-form-message-error">{erreur}</div>}

          {/* À la création comme à la modification : le texte collé remplit la fiche. */}
          <RemplirParTexte
              variante="param"
              contexte="fiche d'un formateur"
              champs={[
                { cle: 'civilite', libelle: 'Civilité', type: 'choix', options: ['Madame', 'Monsieur'] },
                { cle: 'prenom', libelle: 'Prénom' },
                { cle: 'nom', libelle: 'Nom' },
                { cle: 'email', libelle: 'Email', type: 'email' },
                { cle: 'telephone', libelle: 'Téléphone', type: 'telephone' },
                { cle: 'fonction', libelle: 'Fonction' },
                { cle: 'specialites', libelle: 'Spécialités' },
                { cle: 'adresse', libelle: 'Adresse' },
                // Les notes internes sont celles de l'équipe : pas au formateur.
                ...(espaceFormateur ? [] : [{ cle: 'notes', libelle: 'Notes internes' }]),
              ]}
              onValeurs={(v) => setForm((f) => ({ ...f, ...v }))}
            />

          {section(
            'CV',
            'Déposez d’abord le CV : son contenu est analysé afin de préremplir automatiquement les champs encore vides de la fiche. Une version anonymisée — sans identité ni coordonnées — est ensuite générée et conservée parmi les pièces du dossier.',
            <>
              <div className="param-field">
                <div className="formateur-piece">
                  <label
                    className={`param-btn param-btn-sm${occupe ? ' formateur-piece-bouton-inactif' : ''}`}
                  >
                    {deposees('cv').length || cv ? 'Choisir un autre fichier' : 'Choisir un fichier'}
                    <input
                      type="file"
                      accept={FORMATS_ACCEPTES}
                      onChange={(e) => {
                        const fichier = (e.target.files || [])[0] || null;
                        e.target.value = '';
                        setCv(fichier);
                        setRetourCv(null);
                        if (fichier) lireCv(fichier);
                      }}
                      disabled={occupe}
                      hidden
                    />
                  </label>
                  <span className="formateur-fichiers">
                    {deposees('cv').map(lienDepose)}
                    {cv && (
                      <span className="formateur-fichier">
                        <button
                          type="button"
                          className="formateur-fichier-apercu"
                          onClick={() => apercuFichier(cv)}
                          title="Aperçu du fichier"
                        >
                          {cv.name}
                        </button>
                        <button
                          type="button"
                          onClick={() => setCv(null)}
                          disabled={occupe}
                          aria-label={`Retirer ${cv.name}`}
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {!cv && deposees('cv').length === 0 && (
                      <span className="formateur-piece-vide">Aucun fichier</span>
                    )}
                  </span>
                </div>
              </div>
              <BarreProgression valeur={progressionIa} libelle="Lecture du CV par l’IA" estimation />
              {retourCv && (
                <div
                  className={`param-form-message ${
                    retourCv.ok ? 'param-form-message-success' : 'param-form-message-error'
                  }`}
                >
                  {retourCv.texte}
                </div>
              )}
            </>
          )}

          {section(
            'Pièces du dossier',
            'Ajoutez tous les fichiers d’un seul geste : attestations, diplômes, permis, RIB… La nature et le libellé de chaque pièce sont déduits du nom du fichier — corrigez le libellé si besoin. Un recto-verso portant le même nom est regroupé en une seule pièce. L’échéance d’une attestation se préremplit en la lisant dans le fichier ; introuvable, elle reste à saisir.',
            <>
              {auDossier.some((piece) => piece.type !== 'cv') && (
                <span className="formateur-fichiers">
                  {auDossier.filter((piece) => piece.type !== 'cv').map(lienDepose)}
                </span>
              )}

              <div>
                <label
                  className={`param-btn param-btn-sm${occupe ? ' formateur-piece-bouton-inactif' : ''}`}
                >
                  + Ajouter des fichiers
                  <input
                    type="file"
                    accept={FORMATS_ACCEPTES}
                    multiple
                    onChange={ajouterPieces}
                    disabled={occupe}
                    hidden
                  />
                </label>
              </div>

              {pieces.map((p) => {
                const nature = NATURES.find((n) => n.type === p.type) || NATURE_AUTRE;
                return (
                  <div className="formateur-piece-ligne" key={p.cle}>
                    <div className="formateur-piece-infos">
                      <input
                        type="text"
                        className="param-input"
                        maxLength={150}
                        value={p.libelle}
                        onChange={(e) => renommerPiece(p.cle, e.target.value)}
                        disabled={occupe}
                        aria-label="Libellé de la pièce"
                      />
                      <div className="formateur-piece-sous">
                        <span
                          className="formateur-piece-nature"
                          title="Nature devinée d'après le nom : elle suit le libellé si vous le corrigez"
                        >
                          {nature.libelle}
                        </span>
                        <span className="formateur-fichiers">
                          {p.fichiers.map((f, i) => (
                            <span className="formateur-fichier" key={`${f.name}-${i}`}>
                              <button
                                type="button"
                                className="formateur-fichier-apercu"
                                onClick={() => apercuFichier(f)}
                                title="Aperçu du fichier"
                              >
                                {f.name}
                              </button>
                              <button
                                type="button"
                                onClick={() => retirerFichierPiece(p.cle, f)}
                                disabled={occupe}
                                aria-label={`Retirer ${f.name}`}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </span>
                      </div>
                    </div>
                    {nature.echeance && (
                      <label className="param-field formateur-piece-echeance">
                        <span className="param-label">Échéance</span>
                        <input
                          type="date"
                          className="param-input"
                          value={p.echeance}
                          onChange={(e) => changerEcheancePiece(p.cle, e.target.value)}
                          disabled={occupe}
                        />
                        {p.lectureEcheance && (
                          <span className="param-hint-mini">Lecture dans le fichier…</span>
                        )}
                        {!p.lectureEcheance && p.echeanceIntrouvable && !p.echeance && (
                          <span className="param-hint-mini">
                            Introuvable dans le fichier : à saisir.
                          </span>
                        )}
                        {!p.lectureEcheance && p.echeance && p.echeanceDemandee && (
                          <span className="param-hint-mini">Lue dans le fichier — vérifiez.</span>
                        )}
                      </label>
                    )}
                  </div>
                );
              })}
              {pieces.length === 0 && (
                <span className="formateur-piece-vide">Aucun fichier choisi pour l’instant.</span>
              )}
            </>
          )}

          {section(
            'Identité',
            null,
            <div className="param-form-row">
              <label className="param-field">
                <span className="param-label">Civilité</span>
                <SelectRecherche className="param-select" value={form.civilite} onChange={changer('civilite')}>
                  <option value="">—</option>
                  <option value="Madame">Madame</option>
                  <option value="Monsieur">Monsieur</option>
                </SelectRecherche>
              </label>
              <label className="param-field param-field-grow">
                <span className="param-label">Prénom</span>
                <input
                  type="text"
                  className="param-input"
                  maxLength={100}
                  value={form.prenom}
                  onChange={changer('prenom')}
                  required
                />
              </label>
              <label className="param-field param-field-grow">
                <span className="param-label">Nom</span>
                <input
                  type="text"
                  className="param-input"
                  maxLength={100}
                  value={form.nom}
                  onChange={changer('nom')}
                  required
                />
              </label>
            </div>
          )}

          {section(
            'Coordonnées',
            null,
            <>
              <div className="param-form-row">
                <label className="param-field param-field-grow">
                  {/* Sans elle, ni convocation, ni rappels, ni lien d'émargement. */}
                  <span className="param-label">Email (obligatoire)</span>
                  <input
                    type="email"
                    className="param-input"
                    maxLength={255}
                    placeholder="prenom.nom@example.fr"
                    value={form.email}
                    onChange={changer('email')}
                    required
                  />
                </label>
                <label className="param-field">
                  <span className="param-label">Téléphone</span>
                  <input
                    type="tel"
                    className="param-input"
                    maxLength={40}
                    value={form.telephone}
                    onChange={changer('telephone')}
                  />
                </label>
              </div>
              <label className="param-field">
                <span className="param-label">Adresse</span>
                <textarea
                  className="param-textarea"
                  rows={2}
                  value={form.adresse}
                  onChange={changer('adresse')}
                />
              </label>
            </>
          )}

          {section(
            'Activité',
            null,
            <>
              <label className="param-field">
                <span className="param-label">Fonction (imprimée sur les documents)</span>
                <input
                  type="text"
                  className="param-input"
                  maxLength={160}
                  placeholder="Ex. : Juriste en droit social et du travail, expertise en SSCT"
                  value={form.fonction}
                  onChange={changer('fonction')}
                />
              </label>
              <label className="param-field">
                <span className="param-label">Spécialités</span>
                <textarea
                  className="param-textarea"
                  rows={2}
                  placeholder="Ex. : SSCT, formation économique, NAO…"
                  value={form.specialites}
                  onChange={changer('specialites')}
                />
              </label>
            </>
          )}

          {!espaceFormateur && section(
            'Suivi interne',
            null,
            <>
              <label className="param-field">
                <span className="param-label">Rémunération par journée (€ HT)</span>
                <input
                  type="number"
                  className="param-input"
                  min="0"
                  step="0.01"
                  placeholder="Imprimée sur son contrat ; vide = « à convenir »"
                  value={form.tarifJournalierHt}
                  onChange={changer('tarifJournalierHt')}
                />
              </label>
              <label className="param-field">
                <span className="param-label">Notes internes</span>
                <textarea
                  className="param-textarea"
                  rows={4}
                  placeholder="Disponibilités, tarifs…"
                  value={form.notes}
                  onChange={changer('notes')}
                />
              </label>
              <label className="param-checkbox-field">
                <input type="checkbox" checked={form.actif} onChange={changer('actif')} />
                <span>Proposé à l&apos;assignation dans la planification</span>
              </label>
            </>
          )}

          {depot && (
            <BarreProgression
              valeur={((depot.fait + avanceEtape / 100) / depot.total) * 100}
              libelle={
                depot.nom
                  ? `Dépôt des pièces : ${depot.nom} (${depot.rang}/${depot.total - 1})${
                      depot.type === 'cv' ? ' — génération du CV anonyme' : ''
                    }`
                  : formateur || idEnregistre
                    ? 'Enregistrement de la fiche'
                    : 'Création de la fiche'
              }
              estimation
            />
          )}

          <div className="param-form-actions">
            <button type="button" className="param-btn" onClick={onClose} disabled={occupe}>
              Annuler
            </button>
            <button type="submit" className="param-btn param-btn-primary" disabled={occupe}>
              {saving ? 'Enregistrement' : formateur || idEnregistre ? 'Enregistrer' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FormateurFormulaire;
